import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { useAuth, authService, AppHeader, navigateWithAuth } from '@bri/shared-components';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const ProjectListPage = lazy(() => import('./pages/ProjectListPage'));
const NewProjectPage = lazy(() => import('./pages/NewProjectPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const TestApiPage = lazy(() => import('./pages/TestApiPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const VerifyCodePage = lazy(() => import('./pages/VerifyCodePage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));

// Get app URLs from environment or use defaults
const USER_DASHBOARD_URL = import.meta.env.VITE_USER_DASHBOARD_URL || 'http://localhost:3000';
const QUAL_ANALYZER_URL = import.meta.env.VITE_QUAL_ANALYZER_URL || 'http://localhost:3001';

function RouteFallback() {
  return (
    <div className="min-h-[360px] flex items-center justify-center">
      <LoadingSpinner />
    </div>
  );
}

function App() {
  const { user, isLoading, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      void authService.sendFrontendTelemetry('frontend.error', 'qual-analyzer', {
        message: event.message,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      void authService.sendFrontendTelemetry('frontend.unhandled_rejection', 'qual-analyzer', {
        reason: typeof event.reason === 'string' ? event.reason : String(event.reason),
      });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is not authenticated, show auth routes
  if (!user) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/signup" element={<SignupPage />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/verify-code" element={<VerifyCodePage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="*" element={<Navigate to="/auth/login" replace />} />
        </Routes>
      </Suspense>
    );
  }

  // Handler functions for AppHeader
  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
  };

  const handleWorkspaceSwitch = async (workspaceId: string) => {
    try {
      const result = await authService.switchWorkspace(workspaceId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to switch workspace');
      }

      await refreshUser();
    } catch (error) {
      console.error('Failed to switch workspace:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to switch workspace');
    }
  };

  const handleNavigateToSettings = () => {
    // Navigate to settings in user-dashboard with cross-domain auth
    navigateWithAuth(`${USER_DASHBOARD_URL}/app/settings`);
  };

  const handleNavigateToProfile = () => {
    // Navigate to profile in user-dashboard with cross-domain auth
    navigateWithAuth(`${USER_DASHBOARD_URL}/app/profile`);
  };

  const handleNavigateToWorkspaces = () => {
    // Navigate to manage workspaces in user-dashboard with cross-domain auth
    navigateWithAuth(`${USER_DASHBOARD_URL}/app/workspaces`);
  };

  const handleNavigateHome = () => {
    navigate('/');
  };

  // Transform workspace format for shared component (same as dashboard)
  const workspaceList = (user.workspaces || []).map(ws => ({
    workspaceId: ws.workspaceId,
    name: ws.name || 'Untitled Workspace',
    role: (ws.role || 'viewer') as 'owner' | 'editor' | 'viewer',
  }));

  const currentWs = user.currentWorkspace ? {
    workspaceId: user.currentWorkspace.workspaceId,
    name: user.currentWorkspace.name || 'Untitled Workspace',
    role: (user.currentWorkspace.role || 'viewer') as 'owner' | 'editor' | 'viewer',
  } : undefined;

  // User is authenticated, show the app with header
  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        appName="Bellomy Qualitative Analyzer"
        currentWorkspace={currentWs}
        workspaces={workspaceList}
        onWorkspaceSwitch={handleWorkspaceSwitch}
        userEmail={user.email}
        userName={user.name}
        onLogout={handleLogout}
        onNavigateToProfile={handleNavigateToProfile}
        onNavigateToSettings={handleNavigateToSettings}
        onNavigateToWorkspaces={handleNavigateToWorkspaces}
        onNavigateHome={handleNavigateHome}
        appUrls={{
          dashboard: USER_DASHBOARD_URL,
          qualAnalyzer: QUAL_ANALYZER_URL,
        }}
        currentApp="qual-analyzer"
      />
      
      <main>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<ProjectListPage />} />
            <Route path="/new" element={<NewProjectPage />} />
            <Route path="/project/:projectId" element={<ProjectDetailPage />} />
            <Route path="/test" element={<TestApiPage />} />
            
            {/* Redirect auth routes to home if already authenticated */}
            <Route path="/auth/*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

export default App;
